import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Tooltip } from "antd";
import { LoadingOutlined, PlayCircleFilled, PauseCircleFilled, DownOutlined } from "@ant-design/icons";
import moment from "moment";
import { getFormattedSeekValue } from "src/utils/helperFunctions";

// [UNB-2165] Audio speech metrics + unexpected-speech markers from the
// video-stream `audio-events` endpoint (`summary` + `markers`, all in ms).
// Rendering rules (frontend contract):
//  - vadCompleted=false → still processing; the numbers are incomplete, never
//    show them as zeros.
//  - null question fields → speech happened outside the quiz; label it so.
//  - a marker is only seekable when it can be placed on the timeline.

const SPEECH_QUESTION_TYPES = ["speech_scripted", "speech_spontaneous"]
// How far apart (ms) a marker's startedAt and an event's (detectedAt - durationMs)
// may be and still be treated as the same speech region.
const MARKER_EVENT_MATCH_TOLERANCE_MS = 1000

// data.audioProctoringReport.cheatingStatus (report API) → tile label + tone.
const CHEATING_STATUS = {
    notCheating: { label: "Not cheating", tone: "ok" },
    maybeCheating: { label: "Possible cheating", tone: "warn" },
    cheating: { label: "Cheating", tone: "flagged" },
}
// data.audioProctoringReport.cheatingStatusReason → short reviewer-facing reason.
const CHEATING_REASON = {
    TRANSCRIPT_EVIDENCE: "transcript evidence",
    TRANSCRIPT_INCONCLUSIVE: "transcript inconclusive",
    UNEXPECTED_SPEECH: "unexpected speech",
    NO_SIGNAL: "no signal found",
}

const formatMs = (ms) => {
    const n = Number(ms)
    if (!Number.isFinite(n) || n < 0) return "—"
    const totalSec = n / 1000
    if (totalSec < 60) return `${totalSec.toFixed(totalSec < 10 ? 1 : 0)}s`
    const m = Math.floor(totalSec / 60)
    const s = Math.round(totalSec % 60)
    if (m < 60) return `${m}m ${s}s`
    const h = Math.floor(m / 60)
    return `${h}h ${m % 60}m ${s}s`
}

const pctOf = (part, whole) => {
    if (!whole || !Number.isFinite(part)) return null
    return Math.round((part / whole) * 100)
}

const formatQuestionType = (type) => {
    if (!type) return ""
    if (type === "mcq") return "MCQ"
    const words = String(type).replace(/_/g, " ")
    return words.charAt(0).toUpperCase() + words.slice(1)
}

const questionLabel = (marker) => {
    if (marker?.questionIndex == null && !marker?.questionId) return "Outside the quiz"
    const idx = marker.questionIndex != null ? `Q${marker.questionIndex}` : "Question"
    const type = formatQuestionType(marker.questionType)
    return type ? `${idx} · ${type}` : idx
}

/**
 * Match each marker back to the VAD event it was cut from so that "Jump" can
 * seek the audio player (whose timeline is the audio recording clock,
 * `event.startTimeMs`). Markers only carry a wall-clock `startedAt`; events
 * carry `detectedAt` = wall-clock END of the speech, so start = detectedAt − durationMs.
 */
const attachSeekSec = (markers, events) => {
    const candidates = (events || [])
        .map((e) => {
            const endTs = e?.detectedAt ? new Date(e.detectedAt).getTime() : NaN
            return {
                startTs: Number.isFinite(endTs) ? endTs - (e.durationMs || 0) : NaN,
                durationMs: e?.durationMs,
                questionId: e?.questionId ?? null,
                startTimeMs: e?.startTimeMs,
            }
        })
        .filter((c) => Number.isFinite(c.startTs) && Number.isFinite(c.startTimeMs))

    return (markers || []).map((marker) => {
        const startTs = marker?.startedAt ? new Date(marker.startedAt).getTime() : NaN
        let seekSec = null
        if (Number.isFinite(startTs)) {
            let best = null
            for (const c of candidates) {
                if (c.durationMs !== marker.durationMs) continue
                const diff = Math.abs(c.startTs - startTs)
                if (diff > MARKER_EVENT_MATCH_TOLERANCE_MS) continue
                if (!best || diff < best.diff) best = { diff, c }
            }
            if (best) seekSec = best.c.startTimeMs / 1000
        }
        return { ...marker, seekSec }
    })
}

const StatTile = ({ label, value, sub, tone }) => (
    <div className={`audio-stat-tile${tone ? ` audio-stat-tile--${tone}` : ""}`}>
        <p className="audio-stat-tile__label text-12">{label}</p>
        <p className="audio-stat-tile__value">{value}</p>
        {sub ? <p className="audio-stat-tile__sub text-12">{sub}</p> : null}
    </div>
)

// Cheating status tile. The report object being null means the report is still
// being generated, so we show a processing state rather than a status.
const CheatingStatusTile = ({ audioProctoringReport }) => {
    if (!audioProctoringReport) {
        return (
            <StatTile
                label="Cheating status"
                value={<span className="audio-stat-tile__processing"><LoadingOutlined className="mr-6" />Processing</span>}
                sub="report not ready yet"
            />
        )
    }
    const status = CHEATING_STATUS[audioProctoringReport.cheatingStatus]
    const reason = CHEATING_REASON[audioProctoringReport.cheatingStatusReason]
        || (audioProctoringReport.cheatingStatusReason
            ? String(audioProctoringReport.cheatingStatusReason).toLowerCase().replace(/_/g, " ")
            : null)
    return (
        <StatTile
            label="Cheating status"
            value={status?.label || "Unknown"}
            sub={reason}
            tone={status?.tone}
        />
    )
}

/**
 * @param audioProctoringReport data.audioProctoringReport from the report API
 *                              (null = still processing)
 * @param activeIndex index of the marker currently playing as a segment (null = none)
 * @param onToggle    (index, marker) → play that marker's region, or pause it if active
 */
const AudioSpeechSummary = ({
    summary,
    markers = [],
    events = [],
    audioProctoringReport = null,
    activeIndex = null,
    onToggle,
}) => {
    const rows = useMemo(() => attachSeekSec(markers, events), [markers, events])

    // Scroll hint: shown while the list has more rows below the fold, hidden
    // once the reviewer reaches the bottom (or when everything already fits).
    const listRef = useRef(null)
    const [hasMoreBelow, setHasMoreBelow] = useState(false)
    const updateScrollHint = useCallback(() => {
        const el = listRef.current
        if (!el) return setHasMoreBelow(false)
        setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8)
    }, [])
    useEffect(() => {
        updateScrollHint()
        window.addEventListener("resize", updateScrollHint)
        return () => window.removeEventListener("resize", updateScrollHint)
    }, [rows.length, summary, updateScrollHint])
    const scrollListDown = () => {
        const el = listRef.current
        if (el) el.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" })
    }

    if (!summary) return null

    const processing = summary.vadCompleted === false

    if (processing) {
        return (
            <section className="audio-speech-summary w-full">
                <div className="audio-speech-processing flex items-center">
                    <LoadingOutlined className="mr-8" />
                    <p className="text-13">
                        Speech detection is still processing. Speech figures will appear once it completes.
                    </p>
                </div>
            </section>
        )
    }

    const totalAudioMs = summary.totalAudioMs ?? 0
    const totalSpeechMs = summary.totalSpeechMs ?? 0
    const unexpectedMs = summary.unexpectedSpeechMs ?? 0
    const speechPct = pctOf(totalSpeechMs, totalAudioMs)
    const unexpectedPct = pctOf(unexpectedMs, totalSpeechMs)

    return (
        <section className="audio-speech-summary w-full">
            <div className="audio-stat-grid">
                <CheatingStatusTile audioProctoringReport={audioProctoringReport} />
                <StatTile label="Total assessment duration" value={formatMs(totalAudioMs)} />
                <StatTile
                    label="Total speech duration detected"
                    value={formatMs(totalSpeechMs)}
                    sub={speechPct != null ? `${speechPct}% of audio` : null}
                />
                <StatTile
                    label="Unexpected speech duration detected"
                    value={formatMs(unexpectedMs)}
                    sub={unexpectedPct != null ? `${unexpectedPct}% of speech` : null}
                    tone={unexpectedMs > 0 ? "flagged" : "ok"}
                />
                <StatTile
                    label="Speech events"
                    value={summary.eventCount ?? 0}
                    sub={summary.longestEventMs != null ? `longest ${formatMs(summary.longestEventMs)}` : null}
                />
            </div>

            <div className="audio-markers">
                <div className="flex items-center justify-between">
                    <p className="text-14 weight-600">
                        Unexpected speech moments
                        <span className="audio-markers__count ml-6">{rows.length}</span>
                    </p>
                </div>
                {
                    rows.length === 0
                        ? <p className="text-13 clr-gray mt-6">No unexpected speech detected.</p>
                        : <div className={`audio-markers__scroller${hasMoreBelow ? " audio-markers__scroller--more" : ""}`}>
                          <div className="audio-markers__list" ref={listRef} onScroll={updateScrollHint}>
                            {rows.map((marker, index) => {
                                const canSeek = marker.seekSec != null && typeof onToggle === "function"
                                const isActive = canSeek && activeIndex === index
                                const partial = marker.unexpectedMs != null
                                    && marker.durationMs != null
                                    && marker.unexpectedMs < marker.durationMs
                                const expectedType = !!marker.questionType && SPEECH_QUESTION_TYPES.includes(marker.questionType)
                                return (
                                    <div
                                        key={`${marker.startedAt || ""}-${index}`}
                                        className={`audio-marker-row${canSeek ? " audio-marker-row--seekable" : ""}${isActive ? " audio-marker-row--active" : ""}`}
                                        onClick={canSeek ? () => onToggle(index, marker) : undefined}
                                        role={canSeek ? "button" : undefined}
                                    >
                                        <span className="audio-marker-row__index text-12">{index + 1}</span>
                                        <div className="audio-marker-row__main">
                                            <p className="text-13 weight-600">
                                                {questionLabel(marker)}
                                                {expectedType ? <span className="clr-gray weight-400"> · speech question</span> : null}
                                            </p>
                                            <p className="text-12 clr-gray">
                                                {marker.seekSec != null
                                                    ? <span className="audio-marker-row__stamp">{getFormattedSeekValue(marker.seekSec)}</span>
                                                    : null}
                                                {marker.seekSec != null ? " · " : ""}
                                                {marker.startedAt ? moment(marker.startedAt).format("hh:mm:ss A") : "Time unknown"}
                                                {marker.videoOffsetMs != null
                                                    ? ` · at ${getFormattedSeekValue(marker.videoOffsetMs / 1000)} on video`
                                                    : ""}
                                            </p>
                                        </div>
                                        <Tooltip
                                            title={partial
                                                ? `${formatMs(marker.unexpectedMs)} unexpected out of a ${formatMs(marker.durationMs)} speech region`
                                                : "Length of unexpected speech"}
                                        >
                                            <span className="audio-marker-row__duration text-12">
                                                {formatMs(marker.unexpectedMs ?? marker.durationMs)}
                                                {partial ? <span className="clr-gray"> / {formatMs(marker.durationMs)}</span> : null}
                                            </span>
                                        </Tooltip>
                                        {
                                            canSeek
                                                ? <span className={`audio-marker-row__jump text-12${isActive ? " audio-marker-row__jump--active" : ""}`}>
                                                    {isActive ? <PauseCircleFilled className="mr-4" /> : <PlayCircleFilled className="mr-4" />}
                                                    {isActive ? "Pause" : "Play"}
                                                </span>
                                                : <Tooltip title="This moment could not be placed on the audio timeline">
                                                    <span className="audio-marker-row__jump audio-marker-row__jump--disabled text-12">—</span>
                                                </Tooltip>
                                        }
                                    </div>
                                )
                            })}
                          </div>
                          {hasMoreBelow && (
                              <button
                                  type="button"
                                  className="audio-markers__scroll-hint text-12"
                                  onClick={scrollListDown}
                                  aria-label="Scroll down for more moments"
                              >
                                  Scroll for more
                                  <DownOutlined className="ml-4" />
                              </button>
                          )}
                        </div>
                }
            </div>
        </section>
    )
}

export default AudioSpeechSummary
